import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {PANEL_TYPES, isTextPanel, textHasContent} from '../state/informeModel';
import {panelSpan} from '../state/panelLayout';
import {PANEL_STATE, isExplainedByNotice, panelRenderState} from '../state/panelRenderState';
import {consolidateWarnings} from '../state/renderWarnings';
import {panelCaveats} from '../state/panelCaveats';
import {panelFilterNotes} from '../state/panelFilterNotes';
import {AUDIENCE} from '../../../utils/filterNotes';
import {PanelShell} from './PanelShell';
import {PanelCaveatsWarning} from './PanelCaveatsWarning';
import {PanelFilterNotesWarning} from './PanelFilterNotesWarning';
import {BarsBody, KpiBody, LineBody, PanelState, PieBody, TableBody} from './PanelBodies';
import {RichTextView} from './text/RichTextView';

const BODIES = {
    [PANEL_TYPES.KPI]: KpiBody,
    [PANEL_TYPES.BARS]: BarsBody,
    [PANEL_TYPES.PIE]: PieBody,
    [PANEL_TYPES.LINE]: LineBody,
    [PANEL_TYPES.TABLE]: TableBody,
};

/**
 * La misma grilla del folio, de SÓLO LECTURA y sin contexto ni dnd: la usan el portal del
 * cliente y el preview de agencia. Recibe los paneles ya en el shape del estado
 * (`templateStateFromLayout(layout).panels`) y el resultado del motor.
 *
 * El span de la grilla va en la MISMA clase que usa la media query (`.inf-sortable`): con el
 * `grid-column` inline en un wrapper sin clase, la regla de ≤ 992 px no lo alcanzaba y en
 * tablet quedaban cuatro KPI de 150 px por fila (QA FE-02).
 */
// Sin `reportFailed`, a diferencia del editor: sus dos llamadores (el portal del cliente y el
// preview de agencia) gatean la grilla por `!error`, así que con el informe caído esta grilla no se
// monta y el parámetro nunca era `true` (review R-reports-fe-12). Un parámetro que no puede cambiar
// de valor miente sobre lo que el componente hace.
export function StaticPanelsGrid({panels, renderResult, fieldsData, loading = false}) {
    const {t} = useTranslation();
    const byId = {};
    ((renderResult && renderResult.panels) || []).forEach((p) => { byId[p.id] = p; });
    const kpiOrder = (panels || []).filter((p) => p.type === PANEL_TYPES.KPI).map((p) => p.uid || p.id);
    // Igual que en el editor: los paneles que fallaron por la causa que el aviso YA explica bajan a
    // «No disponible» en vez de repetir el párrafo rojo (review R-reports-fe-03).
    const noticeCode = useMemo(() => {
        const notice = consolidateWarnings(renderResult);
        return notice ? notice.code : null;
    }, [renderResult]);
    return (
        <div className={`inf-grid ${loading ? '-loading' : ''}`} data-testid="informes-static-grid">
            {(panels || []).map((panel) => {
                // Un bloque de texto sale del layout (no del motor), también en el modo foto: es lo
                // último que guardó la agencia. Uno vacío no se muestra.
                if (isTextPanel(panel)) {
                    if (!textHasContent(panel.content)) return null;
                    return (
                        <div key={panel.uid || panel.id} className="inf-sortable"
                             style={{gridColumn: `span ${panelSpan(panel)}`, minWidth: 0}}>
                            <PanelShell panel={panel} state={PANEL_STATE.OK} readOnly fieldsData={fieldsData}>
                                <RichTextView html={panel.content}/>
                            </PanelShell>
                        </div>
                    );
                }
                const Body = BODIES[panel.type];
                if (!Body) return null;
                const rendered = byId[panel.id] || byId[panel.uid] || null;
                const state = panelRenderState(rendered, {loading, hasResult: !!renderResult, noticeCode, panel});
                const opensTo = panel.type === PANEL_TYPES.KPI ? 'left' : 'right';
                // Las notas de filtro las lee también el cliente: el «—» que ve es de él, y las notas
                // van en la voz del portal (sin «revisa sus UTM»). Sin paleta, las métricas del bloque
                // salen de lo que devolvió el motor o del propio panel.
                const filterNotes = panelFilterNotes(panel, rendered, null, fieldsData, t, AUDIENCE.PORTAL);
                const showBody = state === PANEL_STATE.OK
                    || (state === PANEL_STATE.EMPTY && !!filterNotes && filterNotes.allNotApplicable);
                return (
                    <div key={panel.uid || panel.id} className="inf-sortable"
                         style={{gridColumn: `span ${panelSpan(panel)}`, minWidth: 0}}>
                        {/* El ⓘ del dato aproximado sí va acá: lo lee el cliente (card ctbIdOOc). Sin
                            paleta, las dimensiones salen de lo que devolvió el motor. */}
                        <PanelShell panel={panel} state={state} readOnly fieldsData={fieldsData}
                                    accentIndex={panel.type === PANEL_TYPES.KPI ? kpiOrder.indexOf(panel.uid || panel.id) : null}
                                    headerWarnings={[
                                        <PanelCaveatsWarning key="caveats" codes={panelCaveats(panel, rendered, null, fieldsData)} opensTo={opensTo}/>,
                                        // Sin lado: el cartel se ancla al encabezado del bloque, así que entra
                                        // siempre (a la derecha se salía en la última columna, QA-VIS-filtrosfe-21).
                                        <PanelFilterNotesWarning key="filter-notes" filterNotes={filterNotes}/>,
                                    ]}>
                            {/* Mismo criterio que el editor: el cuerpo sólo con datos (o con «no aplica»). */}
                            {showBody
                                ? <Body data={rendered ? rendered.data : null} fieldsData={fieldsData} panel={panel} filterNotes={filterNotes}/>
                                : <PanelState state={state} errorCode={rendered ? rendered.error_code : null}
                                              explainedAbove={isExplainedByNotice(rendered, noticeCode)}/>}
                        </PanelShell>
                    </div>
                );
            })}
        </div>
    );
}
